/* Master · Medicine Request Approvals — the master-data team's side of the flow.
   The queue opens on PENDING: the reviewer reads what the requester submitted and
   either approves it (optionally attaching the master medicine code) or rejects it
   with a mandatory reason. Both decisions notify the requester; the email only
   goes out when the original request was raised with an "@"-shaped X-User.

   Backend: POST v1/medicine-requests/{id}/approve | /reject. A request can only be
   decided out of PENDING — a second decision is refused, so the drawer shows the
   decision read-only once the request has moved on. */
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import supplyService from "../../Services/supplyService";
import {
  Button,
  Input,
  SectionHeader,
  ToolbarBar,
  TabBar,
  KpiBox,
  LoadingState,
  ErrorState,
  InlineAlert,
  IconCheck,
  IconX,
  IconClock,
  IconAlert,
} from "../Supply/supplyKit";
import {
  Drawer,
  Field,
  RequestDetail,
  RequestPager,
  RequestsTable,
  StatusPill,
  decisionError,
  fieldInput,
  useDebounced,
  useMedicineRequestCounts,
  useMedicineRequests,
} from "./medicineRequestKit";
import { WarehouseSelect } from "../Supply/warehousePicker";

const TABS = [
  { id: "PENDING", label: "Pending" },
  { id: "APPROVED", label: "Approved" },
  { id: "REJECTED", label: "Rejected" },
  { id: "", label: "All" },
];

const MedicineRequestApprovals = () => {
  const [tab, setTab] = useState("PENDING");
  const [q, setQ] = useState("");
  const search = useDebounced(q);
  /* Only admins reach this screen (RequireAdmin + the hidden nav entry), and they
     review the queue across every warehouse — with a filter to narrow it.

     Deliberately NOT scoped to the app-wide store selection, unlike every other
     screen: this is the one place the job is not "work in a store" but "clear a
     queue that arrives from all of them". Scoping it would hide pending requests
     behind a store switch, which is how a request sits for a week. */
  const [warehouseId, setWarehouseId] = useState("all");
  const [warehouses, setWarehouses] = useState([]);
  const [reviewing, setReviewing] = useState(null);

  const query = useMedicineRequests({ status: tab, search, warehouseId });
  const [countsKey, setCountsKey] = useState(0);
  const counts = useMedicineRequestCounts({ search, warehouseId, reloadKey: countsKey });

  useEffect(() => {
    let alive = true;
    supplyService.getWarehouses().then((list) => {
      if (alive) setWarehouses(list || []);
    });
    return () => {
      alive = false;
    };
  }, []);

  const refresh = () => {
    query.reload();
    setCountsKey((k) => k + 1);
  };

  return (
    <div className="cb-fade-in" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <SectionHeader
        title="Medicine Request Approvals"
        sub="Review requests to add a medicine to the master — approve with a code, or reject with a reason"
        actions={
          <Button size="sm" onClick={refresh} disabled={query.loading}>
            Refresh queue
          </Button>
        }
      />

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10 }}>
        <KpiBox
          label="Awaiting review"
          value={counts.PENDING ?? "—"}
          sub="Requesters are waiting on these"
          variant={counts.PENDING > 0 ? "amber" : "ink"}
        />
        <KpiBox label="Approved" value={counts.APPROVED ?? "—"} sub="Added to the master" variant="green" />
        <KpiBox label="Rejected" value={counts.REJECTED ?? "—"} sub="Duplicate or invalid" variant="red" />
      </div>

      <TabBar
        tabs={TABS.map((t) => ({
          ...t,
          count: t.id ? counts[t.id] : undefined,
          alert: t.id === "PENDING" && counts.PENDING > 0 ? "amber" : undefined,
        }))}
        value={tab}
        onChange={setTab}
      />

      <ToolbarBar>
        <Input
          placeholder="Search medicine name / request number…"
          style={{ width: 320, maxWidth: "100%" }}
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <WarehouseSelect
          value={warehouseId}
          onChange={setWarehouseId}
          warehouses={warehouses}
          noneValue="all"
        />
        <div style={{ marginLeft: "auto", fontSize: 11, color: "var(--fg-muted)", fontFamily: "var(--font-mono)" }}>
          {query.totalElements} request{query.totalElements !== 1 ? "s" : ""}
        </div>
      </ToolbarBar>

      {query.loading ? (
        <LoadingState label="Loading the review queue…" />
      ) : query.error ? (
        <ErrorState message={query.error} onRetry={refresh} />
      ) : (
        <>
          <RequestsTable
            rows={query.rows}
            onOpen={setReviewing}
            emptyHint={
              tab === "PENDING"
                ? "Nothing to review — the queue is clear."
                : "No requests match this filter."
            }
          />
          {query.totalElements > 0 && <RequestPager q={query} />}
        </>
      )}

      {reviewing && (
        <ReviewDrawer
          /* Keyed by request so the decision fields never carry over between rows. */
          key={reviewing.id ?? reviewing.requestNumber}
          req={reviewing}
          onClose={() => setReviewing(null)}
          onDecided={(updated) => {
            /* Keep the drawer open on the decided request so the reviewer sees
               the outcome; the list behind it refreshes to drop it from PENDING. */
            setReviewing(updated);
            refresh();
          }}
        />
      )}
    </div>
  );
};

/* ---------------- Review / decide ---------------- */
const ReviewDrawer = ({ req, onClose, onDecided }) => {
  const pending = req.status === "PENDING";
  const [mode, setMode] = useState(null); // null | "approve" | "reject"
  const [medicineCode, setMedicineCode] = useState("");
  const [remarks, setRemarks] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const reasonOk = reason.trim().length > 0;

  const decide = () => {
    setBusy(true);
    setErr(null);
    const call =
      mode === "approve"
        ? supplyService.approveMedicineRequest(req.id, { medicineCode, remarks })
        : supplyService.rejectMedicineRequest(req.id, { reason });
    call
      .then((updated) => {
        setBusy(false);
        setMode(null);
        toast.success(
          mode === "approve"
            ? `Approved${medicineCode.trim() ? ` — ${medicineCode.trim()}` : ""}. The requester has been notified.`
            : "Rejected. The requester has been notified with your reason."
        );
        /* Prefer the row the backend returns — it carries reviewedBy/reviewedAt.
           Fall back to a locally patched copy if the response body is thin, so
           the drawer still shows the outcome. */
        onDecided?.(
          updated?.id
            ? updated
            : {
                ...req,
                status: mode === "approve" ? "APPROVED" : "REJECTED",
                medicineCode: mode === "approve" ? medicineCode.trim() : req.medicineCode,
                reviewRemarks: mode === "approve" ? remarks.trim() : reason.trim(),
              }
        );
      })
      .catch((e) => {
        setBusy(false);
        setErr(decisionError(e));
      });
  };

  return (
    <Drawer
      width={620}
      icon={pending ? <IconClock size={18} /> : <IconCheck size={18} />}
      title={req.medicineName || "Medicine request"}
      sub={
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
          {req.requestNumber || "—"}
          <StatusPill status={req.status} />
        </span>
      }
      onClose={onClose}
      footer={
        pending ? (
          <>
            <Button onClick={onClose}>Close</Button>
            <div style={{ flex: 1 }} />
            <Button
              variant="danger"
              icon={<IconX size={14} />}
              disabled={busy}
              onClick={() => {
                setErr(null);
                setMode("reject");
              }}
            >
              Reject
            </Button>
            <Button
              variant="primary"
              icon={<IconCheck size={14} />}
              disabled={busy}
              onClick={() => {
                setErr(null);
                setMode("approve");
              }}
            >
              Approve
            </Button>
          </>
        ) : (
          <>
            <Button onClick={onClose}>Close</Button>
            <div style={{ flex: 1 }} />
            <span style={{ fontSize: 11, color: "var(--fg-muted)" }}>
              Already {req.status.toLowerCase()} — decisions are final.
            </span>
          </>
        )
      }
    >
      {/* The decision form sits above the detail while open, so the reviewer
          types with the request still in view below. */}
      {mode === "approve" && (
        <DecisionPanel
          tone="green"
          title="Approve this request"
          hint="Leave the code blank if the master entry doesn't exist yet — the request is still approved."
          busy={busy}
          confirmLabel={busy ? "Approving…" : "Confirm approval"}
          confirmIcon={<IconCheck size={14} />}
          confirmVariant="primary"
          canConfirm
          onCancel={() => setMode(null)}
          onConfirm={decide}
        >
          <Field label="Medicine code">
            <input
              value={medicineCode}
              onChange={(e) => setMedicineCode(e.target.value)}
              maxLength={100}
              placeholder="MED-100234"
              style={{ ...fieldInput, fontFamily: "var(--font-mono)" }}
            />
          </Field>
          <Field label="Remarks" wide>
            <textarea
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              maxLength={500}
              rows={2}
              placeholder="Added to master, use this code from tomorrow"
              style={{ ...fieldInput, height: "auto", padding: 8, resize: "vertical" }}
            />
          </Field>
        </DecisionPanel>
      )}

      {mode === "reject" && (
        <DecisionPanel
          tone="red"
          title="Reject this request"
          hint="The reason is mandatory and is sent to the requester as-is."
          busy={busy}
          confirmLabel={busy ? "Rejecting…" : "Confirm rejection"}
          confirmIcon={<IconX size={14} />}
          confirmVariant="danger"
          canConfirm={reasonOk}
          onCancel={() => setMode(null)}
          onConfirm={decide}
        >
          <Field label="Reason *" wide hint={`${reason.length}/500`}>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={500}
              rows={3}
              placeholder="Duplicate of MED-100112 (Dolo 650, Micro Labs) — use existing code"
              style={{
                ...fieldInput,
                height: "auto",
                padding: 8,
                resize: "vertical",
                border: reasonOk ? fieldInput.border : "1px solid var(--red-500)",
              }}
            />
          </Field>
        </DecisionPanel>
      )}

      {err && (
        <div style={{ marginBottom: 14 }}>
          <InlineAlert>{err}</InlineAlert>
        </div>
      )}

      <RequestDetail req={req} />
    </Drawer>
  );
};

/* Inline confirm block for either decision — a tinted card with its own fields
   and its own confirm button, so a mis-click can't decide a request. */
const DecisionPanel = ({
  tone,
  title,
  hint,
  children,
  busy,
  canConfirm,
  confirmLabel,
  confirmIcon,
  confirmVariant,
  onCancel,
  onConfirm,
}) => (
  <div
    style={{
      border: `1px solid var(--${tone}-100, var(--border-default))`,
      background: `var(--${tone}-50, var(--bg-subtle))`,
      borderRadius: 6,
      padding: 14,
      marginBottom: 16,
    }}
  >
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
      <IconAlert size={14} style={{ color: `var(--${tone}-700)` }} />
      <span style={{ fontSize: 13, fontWeight: 700, color: `var(--${tone}-700)` }}>{title}</span>
    </div>
    <div style={{ fontSize: 11, color: "var(--fg-muted)", marginBottom: 12 }}>{hint}</div>
    <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 12 }}>{children}</div>
    <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
      <Button size="sm" onClick={onCancel} disabled={busy}>
        Cancel
      </Button>
      <div style={{ flex: 1 }} />
      <Button
        size="sm"
        variant={confirmVariant}
        icon={confirmIcon}
        disabled={busy || !canConfirm}
        onClick={onConfirm}
      >
        {confirmLabel}
      </Button>
    </div>
  </div>
);

export default MedicineRequestApprovals;
